import { getSelectedPropertyId } from '@/auth/selectedPropertyStore';

const STORAGE_KEY = 'nangbook-offline-mutation-queue';
const BASE_RETRY_DELAY_MS = 30_000;
const MAX_RETRY_DELAY_MS = 30 * 60 * 1000;
const STALE_SYNCING_MS = 2 * 60 * 1000;

export type OfflineMutationStatus = 'queued' | 'syncing' | 'failed' | 'conflicted';

export interface OfflineMutation {
  id: string;
  clientOperationId: string;
  path: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body: unknown;
  resource: string;
  propertyId: number | null;
  status: OfflineMutationStatus;
  retryCount: number;
  lastError: string | null;
  nextRetryAt: string | null;
  lastAttemptAt: string | null;
  lastKnownUpdatedAt: string | null;
  createdAt: string;
}

export interface QueueOfflineMutationInput {
  path: string;
  method: OfflineMutation['method'];
  body: unknown;
  clientOperationId: string;
  resource: string;
  lastKnownUpdatedAt?: string | null;
}

export function queueOfflineMutation(input: QueueOfflineMutationInput): OfflineMutation {
  const existingQueue = readOfflineQueue();
  const compacted = compactOfflineQueue(existingQueue, input);

  const mutation: OfflineMutation = {
    id: input.clientOperationId,
    clientOperationId: input.clientOperationId,
    path: input.path,
    method: input.method,
    body: input.body,
    resource: input.resource,
    propertyId: getSelectedPropertyId(),
    status: 'queued',
    retryCount: 0,
    lastError: null,
    nextRetryAt: null,
    lastAttemptAt: null,
    lastKnownUpdatedAt: input.lastKnownUpdatedAt ?? null,
    createdAt: new Date().toISOString(),
  };

  writeQueue([...compacted, mutation]);

  return mutation;
}

export function readOfflineQueue(): OfflineMutation[] {
  if (typeof window === 'undefined') return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.map(normalizeMutation).filter(isOfflineMutation) : [];
  } catch {
    return [];
  }
}

export function markOfflineMutationSyncing(id: string): void {
  updateOfflineMutation(id, {
    status: 'syncing',
    lastAttemptAt: new Date().toISOString(),
  });
}

export function markOfflineMutationRetry(id: string, error: string): void {
  const mutation = readOfflineQueue().find((candidate) => candidate.id === id);
  const retryCount = (mutation?.retryCount ?? 0) + 1;
  const delay = Math.min(BASE_RETRY_DELAY_MS * 2 ** Math.max(retryCount - 1, 0), MAX_RETRY_DELAY_MS);

  updateOfflineMutation(id, {
    status: 'queued',
    retryCount,
    lastError: error,
    nextRetryAt: new Date(Date.now() + delay).toISOString(),
  });
}

export function markOfflineMutationFailed(id: string, error: string): void {
  updateOfflineMutation(id, {
    status: 'failed',
    lastError: error,
    nextRetryAt: null,
  });
}

export function markOfflineMutationConflicted(id: string, error: string): void {
  updateOfflineMutation(id, {
    status: 'conflicted',
    lastError: error,
    nextRetryAt: null,
  });
}

export function removeOfflineMutation(id: string): void {
  writeQueue(readOfflineQueue().filter((mutation) => mutation.id !== id));
}

function updateOfflineMutation(id: string, patch: Partial<OfflineMutation>): void {
  writeQueue(readOfflineQueue().map((mutation) => (
    mutation.id === id ? { ...mutation, ...patch } : mutation
  )));
}

function writeQueue(queue: OfflineMutation[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  window.dispatchEvent(new CustomEvent('nangbook-offline-queue-changed'));
}

function compactOfflineQueue(queue: OfflineMutation[], input: QueueOfflineMutationInput): OfflineMutation[] {
  if (!['PUT', 'PATCH'].includes(input.method)) {
    return queue;
  }

  return queue.filter((mutation) => !(
    ['queued', 'failed'].includes(mutation.status)
      && mutation.method === input.method
      && mutation.path === input.path
      && mutation.propertyId === getSelectedPropertyId()
  ));
}

function normalizeMutation(value: unknown): OfflineMutation | null {
  if (!value || typeof value !== 'object') return null;

  const mutation = value as Partial<OfflineMutation>;
  const id = typeof mutation.id === 'string'
    ? mutation.id
    : typeof mutation.clientOperationId === 'string'
      ? mutation.clientOperationId
      : createClientOperationId();

  if (!mutation.path || !mutation.method) return null;

  return {
    id,
    clientOperationId: mutation.clientOperationId ?? id,
    path: mutation.path,
    method: mutation.method,
    body: mutation.body,
    resource: mutation.resource ?? inferResourceFromPath(mutation.path),
    propertyId: mutation.propertyId ?? null,
    status: normalizeStatus(mutation),
    retryCount: mutation.retryCount ?? 0,
    lastError: mutation.lastError ?? null,
    nextRetryAt: mutation.nextRetryAt ?? null,
    lastAttemptAt: mutation.lastAttemptAt ?? null,
    lastKnownUpdatedAt: mutation.lastKnownUpdatedAt ?? null,
    createdAt: mutation.createdAt ?? new Date().toISOString(),
  };
}

function isOfflineMutation(value: OfflineMutation | null): value is OfflineMutation {
  return value !== null;
}

function normalizeStatus(mutation: Partial<OfflineMutation>): OfflineMutationStatus {
  if (mutation.status !== 'syncing') {
    return mutation.status ?? 'queued';
  }

  if (!mutation.lastAttemptAt || Number.isNaN(Date.parse(mutation.lastAttemptAt))) {
    return 'queued';
  }

  return Date.now() - Date.parse(mutation.lastAttemptAt) > STALE_SYNCING_MS ? 'queued' : 'syncing';
}

export function createClientOperationId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
}

export function inferResourceFromPath(path: string): string {
  const [resource = 'unknown'] = path.replace(/^\/+/, '').split('/');

  return resource || 'unknown';
}
